/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";


/* =========================
   PRESENTPLUS BRANDING
   Uses the school colour from AdminManager CSS vars.
   Falls back safely if the old shell has not been updated yet.
========================= */
const BRAND = "var(--pp-brand-primary, var(--pp-primary, #4f46e5))";
const BRAND_DARK = "var(--pp-brand-dark, var(--pp-primary, #3730a3))";
const BRAND_SOFT = "var(--pp-brand-soft, color-mix(in srgb, var(--pp-brand-primary, var(--pp-primary, #4f46e5)) 12%, white))";
const BRAND_BORDER = "var(--pp-brand-border, color-mix(in srgb, var(--pp-brand-primary, var(--pp-primary, #4f46e5)) 28%, white))";
const BRAND_SHADOW = "var(--pp-brand-shadow, color-mix(in srgb, var(--pp-brand-primary, var(--pp-primary, #4f46e5)) 24%, transparent))";
const BRAND_GRADIENT = "linear-gradient(135deg, var(--pp-brand-primary, var(--pp-primary, #2563eb)), var(--pp-brand-dark, var(--pp-primary, #2563eb)))";

const STATUS_ORDER = ["all", "pending", "accepted", "approved", "rejected", "revoked", "expired"];

export default function ParentInviteDashboard({ schoolId }) {
  const currentSchoolId = schoolId || "school_001";

  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const loadInvites = async () => {
    setLoading(true);
    setError("");

    try {
      const snap = await getDocs(
        collection(db, "schools", currentSchoolId, "invites")
      );

      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((x) => String(x.role || "").toLowerCase() === "parent")
        .sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

      setInvites(list);
    } catch (err) {
      console.error(err);
      setError("Failed to load parent invites.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentSchoolId) return;
    loadInvites();
  }, [currentSchoolId]);

  const updateInvite = async (invite, status) => {
    setBusyId(invite.id);
    setError("");

    try {
      await setDoc(
        doc(db, "schools", currentSchoolId, "invites", invite.id),
        {
          status,
          [`${status}At`]: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      if (invite.parentId) {
        await setDoc(
          doc(db, "schools", currentSchoolId, "parents", invite.parentId),
          {
            status: status === "approved" ? "active" : status,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      await loadInvites();
    } catch (err) {
      console.error(err);
      setError(`Failed to ${status} invite.`);
    } finally {
      setBusyId("");
    }
  };

  const handleDelete = async (invite) => {
    const ok = window.confirm(
      `Delete invite for ${invite.name || invite.email || "this parent"}?`
    );
    if (!ok) return;

    setBusyId(invite.id);
    setError("");

    try {
      await deleteDoc(doc(db, "schools", currentSchoolId, "invites", invite.id));
      await loadInvites();
    } catch (err) {
      console.error(err);
      setError("Failed to delete invite.");
    } finally {
      setBusyId("");
    }
  };

  const counts = useMemo(
    () => ({
      total: invites.length,
      pending: invites.filter((i) => i.status === "pending").length,
      accepted: invites.filter((i) => i.status === "accepted").length,
      approved: invites.filter((i) => i.status === "approved").length,
      rejected: invites.filter((i) => i.status === "rejected").length,
    }),
    [invites]
  );

  const filteredInvites = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();

    return invites.filter((invite) => {
      const statusOk =
        statusFilter === "all" ? true : invite.status === statusFilter;

      const textOk = !q
        ? true
        : [
            invite.name,
            invite.email,
            invite.status,
            invite.parentId,
            ...(Array.isArray(invite.linkedStudentIds)
              ? invite.linkedStudentIds
              : []),
          ]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q));

      return statusOk && textOk;
    });
  }, [invites, query, statusFilter]);

  return (
    <div style={page}>
      <div style={heroCard}>
        <div style={heroTextWrap}>
          <div style={eyebrow}>PresentPlus Admin</div>
          <h1 style={heroTitle}>Parent Invite Dashboard</h1>
          <p style={heroSub}>
            Review parent onboarding, track approval stages, and keep access
            clean and controlled.
          </p>
        </div>

        <button
          type="button"
          style={refreshBtn}
          onClick={loadInvites}
          disabled={loading}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {error ? <div style={errorBanner}>{error}</div> : null}

      <div style={statsGrid}>
        <StatCard label="Total Invites" value={counts.total} tone="neutral" note="All parent invite records" />
        <StatCard label="Pending" value={counts.pending} tone="pending" note="Awaiting parent response" />
        <StatCard label="Accepted" value={counts.accepted} tone="accepted" note="Signed in, awaiting admin" />
        <StatCard label="Approved" value={counts.approved} tone="approved" note="Access granted" />
        <StatCard label="Rejected" value={counts.rejected} tone="rejected" note="Access denied" />
      </div>

      <div style={toolbar}>
        <div style={searchBox}>
          <span style={searchIcon}>⌕</span>
          <input
            style={searchInput}
            placeholder="Search by parent name, email, status, or student ID"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div style={filterRow}>
          {STATUS_ORDER.map((key) => (
            <button
              key={key}
              type="button"
              style={pillBtn(statusFilter === key)}
              onClick={() => setStatusFilter(key)}
            >
              {capitalize(key)}
            </button>
          ))}
        </div>
      </div>

      <div style={tableCard}>
        <div style={tableHeadBar}>
          <div>
            <h2 style={sectionTitle}>Invite Queue</h2>
            <p style={sectionSub}>
              {loading
                ? "Loading invites..."
                : `${filteredInvites.length} invite${filteredInvites.length === 1 ? "" : "s"} shown`}
            </p>
          </div>
        </div>

        {loading ? (
          <div style={emptyWrap}>
            <div style={emptyTitle}>Loading parent invites...</div>
          </div>
        ) : filteredInvites.length === 0 ? (
          <div style={emptyWrap}>
            <div style={emptyTitle}>No matching invites</div>
            <div style={emptySub}>Try changing the search or filter selection.</div>
          </div>
        ) : (
          <div style={desktopTableWrap}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>Parent</th>
                  <th style={th}>Linked Students</th>
                  <th style={th}>Timeline</th>
                  <th style={th}>Status</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvites.map((invite) => {
                  const busy = busyId === invite.id;
                  return (
                    <tr key={invite.id}>
                      <td style={td}>
                        <div style={personCell}>
                          <div style={avatarBubble}>
                            {getInitials(invite.name || invite.email || "P")}
                          </div>
                          <div>
                            <div style={personName}>{invite.name || "Unnamed Parent"}</div>
                            <div style={personMeta}>{invite.email || "No email"}</div>
                          </div>
                        </div>
                      </td>

                      <td style={td}>
                        <StudentChips ids={invite.linkedStudentIds} />
                      </td>

                      <td style={td}>
                        <div style={timelineList}>
                          <TimelineItem label="Created" value={fmtTs(invite.createdAt)} />
                          <TimelineItem label="Accepted" value={fmtTs(invite.acceptedAt)} />
                          <TimelineItem label="Approved" value={fmtTs(invite.approvedAt)} />
                          <TimelineItem label="Rejected" value={fmtTs(invite.rejectedAt)} />
                        </div>
                      </td>

                      <td style={td}>
                        <StatusBadge status={invite.status} />
                      </td>

                      <td style={td}>
                        <div style={actionsWrap}>
                          {invite.status !== "approved" && (
                            <button
                              type="button"
                              style={approveBtn}
                              disabled={busy}
                              onClick={() => updateInvite(invite, "approved")}
                            >
                              {busy ? "Working..." : "Approve"}
                            </button>
                          )}

                          {invite.status !== "rejected" && (
                            <button
                              type="button"
                              style={rejectBtn}
                              disabled={busy}
                              onClick={() => updateInvite(invite, "rejected")}
                            >
                              {busy ? "Working..." : "Reject"}
                            </button>
                          )}

                          <button
                            type="button"
                            style={deleteBtn}
                            disabled={busy}
                            onClick={() => handleDelete(invite)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, note, tone = "neutral" }) {
  const tones = {
    neutral: { bg: "linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%)", ring: "rgba(15, 23, 42, 0.08)", value: "#0f172a" },
    pending: { bg: "linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)", ring: "rgba(245, 158, 11, 0.2)", value: "#b45309" },
    accepted: { bg: "linear-gradient(135deg, #eff6ff 0%, #f5f9ff 100%)", ring: "rgba(59, 130, 246, 0.2)", value: "#1d4ed8" },
    approved: { bg: "linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)", ring: "rgba(16, 185, 129, 0.2)", value: "#047857" },
    rejected: { bg: "linear-gradient(135deg, #fff1f2 0%, #fef2f2 100%)", ring: "rgba(239, 68, 68, 0.2)", value: "#b91c1c" },
  };
  const t = tones[tone] || tones.neutral;

  return (
    <div style={{ ...statCard, background: t.bg, boxShadow: `0 10px 30px ${t.ring}` }}>
      <div style={statLabel}>{label}</div>
      <div style={{ ...statValue, color: t.value }}>{value}</div>
      <div style={statNote}>{note}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = String(status || "unknown").toLowerCase();
  const map = {
    pending: { bg: "#fff7ed", fg: "#b45309", border: "#fed7aa" },
    accepted: { bg: "#eff6ff", fg: "#1d4ed8", border: "#bfdbfe" },
    approved: { bg: "#ecfdf5", fg: "#047857", border: "#a7f3d0" },
    rejected: { bg: "#fef2f2", fg: "#b91c1c", border: "#fecaca" },
    unknown: { bg: "#f3f4f6", fg: "#374151", border: "#d1d5db" },
  };
  const tone = map[s] || map.unknown;

  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 12px",
      borderRadius: 999,
      background: tone.bg,
      color: tone.fg,
      border: `1px solid ${tone.border}`,
      fontSize: 12,
      fontWeight: 800,
      whiteSpace: "nowrap",
    }}>
      <span style={{ width: 8, height: 8, borderRadius: 999, background: tone.fg, display: "inline-block" }} />
      {capitalize(s)}
    </span>
  );
}

function TimelineItem({ label, value }) {
  return (
    <div style={timelineItem}>
      <span style={timelineKey}>{label}</span>
      <span style={timelineValue}>{value || "—"}</span>
    </div>
  );
}

function StudentChips({ ids }) {
  const list = Array.isArray(ids) ? ids : [];
  if (!list.length) return <span style={mutedText}>No linked students</span>;

  return (
    <div style={chipsWrap}>
      {list.slice(0, 4).map((id) => (
        <span key={id} style={chip}>{id}</span>
      ))}
      {list.length > 4 ? <span style={chipMuted}>+{list.length - 4} more</span> : null}
    </div>
  );
}

function fmtTs(ts) {
  try {
    if (!ts?.toDate) return "";
    return ts.toDate().toLocaleString();
  } catch {
    return "";
  }
}

function getInitials(value) {
  const str = String(value || "").trim();
  if (!str) return "P";
  const bits = str.split(/\s+/).slice(0, 2);
  return bits.map((b) => b[0]?.toUpperCase() || "").join("") || "P";
}

function capitalize(value) {
  const s = String(value || "");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const page = { minHeight: "100%", padding: 24, background: "transparent" };
const heroCard = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap", padding: 24, borderRadius: 24, background: BRAND_GRADIENT, color: "#fff", boxShadow: `0 20px 48px ${BRAND_SHADOW}`, marginBottom: 20 };
const heroTextWrap = { maxWidth: 720 };
const eyebrow = { fontSize: 12, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.8, marginBottom: 10 };
const heroTitle = { margin: 0, fontSize: 32, fontWeight: 900, lineHeight: 1.08 };
const heroSub = { margin: "10px 0 0", maxWidth: 640, fontSize: 14, lineHeight: 1.6, color: "rgba(255,255,255,0.82)" };
const refreshBtn = { border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.12)", color: "#fff", padding: "12px 16px", borderRadius: 14, fontWeight: 800, cursor: "pointer", backdropFilter: "blur(10px)" };
const errorBanner = { marginBottom: 16, padding: "14px 16px", borderRadius: 16, border: "1px solid #fecaca", background: "#fff1f2", color: "#b91c1c", fontWeight: 700 };
const statsGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 20 };
const statCard = { padding: 18, borderRadius: 20, border: "1px solid rgba(15,23,42,0.06)" };
const statLabel = { fontSize: 12, color: "#64748b", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em" };
const statValue = { marginTop: 10, fontSize: 32, fontWeight: 900, lineHeight: 1 };
const statNote = { marginTop: 10, fontSize: 12, color: "#64748b", lineHeight: 1.45 };
const toolbar = { display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 12, marginBottom: 20 };
const searchBox = { display: "flex", alignItems: "center", gap: 10, padding: "0 14px", borderRadius: 18, background: "#fff", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 8px 24px rgba(15,23,42,0.05)", minHeight: 56 };
const searchIcon = { color: "#64748b", fontSize: 16 };
const searchInput = { flex: 1, border: "none", outline: "none", fontSize: 14, background: "transparent", color: "#0f172a" };
const filterRow = { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end" };
const pillBtn = (active) => ({ padding: "10px 14px", borderRadius: 999, border: active ? "1px solid transparent" : "1px solid rgba(15,23,42,0.08)", background: active ? BRAND_GRADIENT : "#fff", color: active ? "#fff" : "#334155", fontWeight: 800, fontSize: 13, cursor: "pointer", boxShadow: active ? `0 10px 24px ${BRAND_SHADOW}` : "none" });
const tableCard = { borderRadius: 24, background: "rgba(255,255,255,0.94)", border: "1px solid rgba(15,23,42,0.06)", boxShadow: "0 24px 50px rgba(15,23,42,0.08)", overflow: "hidden" };
const tableHeadBar = { padding: "22px 22px 12px", borderBottom: "1px solid rgba(15,23,42,0.06)" };
const sectionTitle = { margin: 0, fontSize: 22, fontWeight: 900, color: "#0f172a" };
const sectionSub = { margin: "6px 0 0", color: "#64748b", fontSize: 13 };
const desktopTableWrap = { overflowX: "auto" };
const table = { width: "100%", borderCollapse: "collapse" };
const th = { textAlign: "left", padding: "14px 18px", borderBottom: "1px solid rgba(15,23,42,0.06)", fontSize: 12, letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b" };
const td = { padding: "16px 18px", borderBottom: "1px solid rgba(15,23,42,0.05)", verticalAlign: "top" };
const personCell = { display: "flex", gap: 12, alignItems: "center" };
const avatarBubble = { width: 42, height: 42, borderRadius: 999, display: "grid", placeItems: "center", background: BRAND_GRADIENT, color: "#fff", fontWeight: 900, fontSize: 14 };
const personName = { fontWeight: 800, color: "#0f172a" };
const personMeta = { marginTop: 4, fontSize: 13, color: "#64748b" };
const timelineList = { display: "grid", gap: 6 };
const timelineItem = { display: "flex", gap: 10, justifyContent: "space-between" };
const timelineKey = { color: "#64748b", fontSize: 12 };
const timelineValue = { color: "#0f172a", fontSize: 12, fontWeight: 700 };
const chipsWrap = { display: "flex", gap: 8, flexWrap: "wrap" };
const chip = { padding: "6px 10px", borderRadius: 999, background: BRAND_SOFT, color: BRAND_DARK, fontSize: 12, fontWeight: 700 };
const chipMuted = { padding: "6px 10px", borderRadius: 999, background: "#f3f4f6", color: "#475569", fontSize: 12, fontWeight: 700 };
const mutedText = { color: "#64748b", fontSize: 13 };
const actionsWrap = { display: "flex", gap: 8, flexWrap: "wrap" };
const approveBtn = { padding: "8px 12px", borderRadius: 12, border: "none", background: BRAND_GRADIENT, color: "#fff", cursor: "pointer", fontWeight: 800 };
const rejectBtn = { padding: "8px 12px", borderRadius: 12, border: "none", background: "#fff1f2", color: "#b91c1c", cursor: "pointer", fontWeight: 800 };
const deleteBtn = { padding: "8px 12px", borderRadius: 12, border: "none", background: "#f3f4f6", color: "#334155", cursor: "pointer", fontWeight: 800 };
const emptyWrap = { padding: 28, textAlign: "center" };
const emptyTitle = { fontWeight: 900, color: "#0f172a" };
const emptySub = { marginTop: 6, color: "#64748b" };